import React from 'react';
import parse from "date-fns/parse";
import DepartureStationPopover
    from "@Pages/stationToStation/features/departureStationPopover/components/DepartureStationPopover";
import BusChangePopover from "@Pages/stationToStation/features/busChangePopover/components/BusChangePopover";
import PricePopover from "@Pages/stationToStation/features/pricePopover/components/PricePopover";
import StationToStationDatePicker from "./StationToStationDatePicker";
import formatDate from "@DateTime/functions/formatDate";
import Translation from "@Translation/components/Translation";
import useFerrySupportedLocale from "@GetByFerry/hooks/useFerrySupportedLocale";
import BestPriceRouteType from "@Pages/stationToStation/types/BestPriceRoute.type";
import IconMTicket from "@Icons/ticket/IconMTicket";
import {Colors} from "@Styles/Colors.style";
import IconRating from "@Icons/IconRating";
import useIsMobile from "@Hooks/selector/useIsMobile";
import useIsSmallTablet from "@Hooks/selector/useIsSmallTablet";
import BestPriceOnDateRouteStations from "@Pages/stationToStation/components/BestPriceOnDateRouteStations";

interface BestPriceOnDateRoutePanelProps {
    route: BestPriceRouteType
    index: number,
}

function BestPriceOnDateRoutePanel({route, index}: BestPriceOnDateRoutePanelProps) {
    const parseAndStripSeconds = (date: string) => (
        formatDate(parse(date, 'HH:mm:ss', new Date()), 'HH:mm')
    )

    const departureTime = parseAndStripSeconds(route.departureTime)
    const arrivalTime = parseAndStripSeconds(route.arrivalTime)
    const {locale, transportType} = useFerrySupportedLocale()
    const isMobile = useIsMobile()
    const isSmallTablet = useIsSmallTablet()

    return (
        <div className="panel panel-default">
            <div
                className={`panel-heading${route.isApi ? ' panel-heading--api ' : ''}`}
                role="tab"
                id={`heading${route.id}`}
                data-route_id={`${route.id}`}
            >
                <div className="gb--route">
                    {(isMobile || isSmallTablet) ? (
                        <div className="gb--route-stations-container">
                            <BestPriceOnDateRouteStations
                                route={route}
                                index={index}
                                departureTime={departureTime}
                                arrivalTime={arrivalTime}
                            />
                        </div>
                    ) : (
                        <BestPriceOnDateRouteStations
                                route={route}
                                index={index}
                                departureTime={departureTime}
                                arrivalTime={arrivalTime}
                        />
                    )}

                    <div className="gb--route-element gb--route-ratings ">
                        <div className="gb--column gb--column company-rating">
                            {route.companyName && (
                                <span className="gb--truncate gb--company-code gb--emphasize-default">
                                    {route.companyName}
                                </span>
                            )}
                            {route.companyRating > 0 && (
                                <div className="gb--row gb--row-justify gb--row-middle">
                                    <div
                                        className="gb--row gb--row-middle company-rating__score"
                                    >
                                        <IconRating fill={Colors.warningColor} stroke={Colors.warningColor}/>
                                        {route.companyRating}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="gb--route-price gb--route-status">
                        <PricePopover
                            index={index}
                            price={route.priceOnDate}
                            preparedPrices={route.preparedPrices}
                            passengerGroups={route.passengerGroups}
                            routeId={route.id}
                        />
                    </div>
                    <div className="gb--route-element">
                        <div className="gb--route-element-badges">
                            {route.hasETicket && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    M-Ticket
                                </span>
                            )}
                            {route.bestRated && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.rating"/>
                                </span>
                            )}
                            {route.cheapest && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.price"/>
                                </span>
                            )}
                            {route.fastest && (
                                <span className="gb--badge gb--badge-left gb--badge-blue">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.travel_time"/>
                                </span>
                            )}
                            {route.bestOfferOn && (
                                <span className="gb--badge gb--badge-left gb--badge-blue">
                                    <Translation translationKey="search_results.best_offer_on"/>
                                    {' '}
                                    {route.bestOfferOn}
                                </span>
                            )}
                        </div>

                        {route.showDatePicker && (
                            <div className="gb--route-datepicker">
                                <StationToStationDatePicker
                                    locale={locale}
                                    routeId={route.id}
                                    transportType={transportType}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BestPriceOnDateRoutePanel;